// Signing the Charter: a name, an email (never shown), an organisation, what the signer wants to
// hear about, and a honeypot field people never see. The products page links here with
// ?interest=… to pre-tick an early-access list.
import { CheckCircle } from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { ApiError, api } from "../../api/client";
import { type Interest, INTERESTS } from "../../api/types";

const INTEREST_LABEL: Record<Interest, string> = {
  broker: "Early access to OCS Broker",
  workflows: "Early access to OCS Workflows",
  cloud: "Running OCS Cloud",
  publish: "Publishing a workflow",
  contribute: "Contributing: writing, standards, code",
};

const ERRORS: Record<string, string> = {
  name: "Please give a name (up to 80 characters).",
  email: "That email address doesn't look right.",
  organisation: "The organisation can be up to 120 characters.",
  interests: "Please pick from the list.",
};

export function SignForm() {
  const [params] = useSearchParams();
  const asked = params.get("interest");
  const [interests, setInterests] = useState<Interest[]>(INTERESTS.includes(asked as Interest) ? [asked as Interest] : []);
  const queries = useQueryClient();
  const sign = useMutation({ mutationFn: api.sign, onSuccess: () => void queries.invalidateQueries({ queryKey: ["charter"] }) });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const text = (k: string) => String(f.get(k) ?? "").trim();
    sign.mutate({
      name: text("name"),
      email: text("email"),
      organisation: text("organisation") || undefined,
      list_publicly: f.get("list_publicly") === "on",
      wants_updates: f.get("wants_updates") === "on",
      interests,
      website: text("website"),
    });
  };

  if (sign.data) {
    return (
      <div className="rounded-3xl bg-ink p-8 text-paper sm:p-10" role="status">
        <CheckCircle weight="fill" className="h-12 w-12 text-signal" aria-hidden />
        <p className="display mt-5 text-4xl">{sign.data.already ? "You'd already signed. Thank you, again." : "Signed. Thank you."}</p>
        <p className="mt-3 text-paper/75">
          You are one of {sign.data.signatures.toLocaleString("en-GB")}. The next step is the one that matters most: send the Charter to one person who should read it.
        </p>
        <Link to="/community" className="btn-signal mt-7">
          Other ways to help
        </Link>
      </div>
    );
  }

  const fieldError = sign.error instanceof ApiError && sign.error.field ? ERRORS[sign.error.field] : undefined;
  const otherError = sign.error && !fieldError ? (sign.error instanceof ApiError && sign.error.status === 429 ? "Too many tries from here. Please wait an hour." : "Something went wrong. Please try again.") : undefined;
  return (
    <form onSubmit={submit} className="card space-y-5 p-6 sm:p-8" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block">
          <span className="text-[14px] font-semibold">Your name</span>
          <input name="name" required maxLength={80} autoComplete="name" className="field mt-1.5" />
        </label>
        <label className="block">
          <span className="text-[14px] font-semibold">Email</span>
          <input name="email" type="email" required maxLength={254} autoComplete="email" className="field mt-1.5" />
        </label>
      </div>
      <label className="block">
        <span className="text-[14px] font-semibold">Organisation</span> <span className="text-[13px] text-ink3">(optional)</span>
        <input name="organisation" maxLength={120} autoComplete="organization" className="field mt-1.5" />
      </label>
      {/* People never see this; form-filling bots do. */}
      <label className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden>
        Website
        <input name="website" tabIndex={-1} autoComplete="off" />
      </label>
      <fieldset>
        <legend className="text-[14px] font-semibold">I'd also like to hear about</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {INTERESTS.map((i) => (
            <label key={i} className="flex items-center gap-2.5 text-[14.5px] text-ink2">
              <input type="checkbox" checked={interests.includes(i)} onChange={(e) => setInterests((xs) => (e.target.checked ? [...xs, i] : xs.filter((x) => x !== i)))} className="h-4 w-4 accent-[rgb(var(--signal))]" />
              {INTEREST_LABEL[i]}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="space-y-2 border-t border-line pt-5">
        <label className="flex items-start gap-2.5 text-[14.5px] text-ink2">
          <input type="checkbox" name="wants_updates" className="mt-1 h-4 w-4 accent-[rgb(var(--signal))]" /> Email me now and then about the Charter and the community.
        </label>
        <label className="flex items-start gap-2.5 text-[14.5px] text-ink2">
          <input type="checkbox" name="list_publicly" className="mt-1 h-4 w-4 accent-[rgb(var(--signal))]" /> When the list of signatories goes public, include my name and organisation.
        </label>
      </div>
      {fieldError || otherError ? (
        <p role="alert" className="rounded-xl bg-signal/10 px-4 py-3 text-[14px] font-medium text-signal">
          {fieldError ?? otherError}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-sm text-[12.5px] text-ink3">
          We never show or share your email. See <Link to="/privacy" className="underline">privacy</Link>.
        </p>
        <button type="submit" disabled={sign.isPending} className="btn-signal disabled:opacity-60">
          {sign.isPending ? "Signing…" : "Sign the Charter"}
        </button>
      </div>
    </form>
  );
}
